import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { Form } from 'react-formio';
import axios from 'axios';
import '../../components/css/FormioOverrides.css';
/* Same Invitation Types layout as Participant Edit Personal Settings */
import '../css/ParticipantEditPersonalSettings.css';
import '../css/FormViewer.css';
import { USER_PORTAL_FORMIO_OPTIONS } from '../../utils/formioFormOptions';
import {
  prepareFormSchemaWithInvitationSelects,
  formSchemaHasInvitationEntitlementLayout,
  stampPersonalSettingsRadioLayoutClasses,
} from '../../utils/invitationTypeFormioUtils';
import {
  applyStandardSchemaTransforms,
  ensureDatagridCellLabels,
  ensureDatagridPreviewRows,
  buildDatagridPreviewSubmissionData,
} from '../../utils/formSchemaUtils';
import { ensureDatagridRowActions } from '../../utils/caddieFormioUtils';
import { formNameToCssClass } from '../../utils/formioFormNameClass';

/** Admin form preview — same options as user portal, no progress/status hooks. */
const FORM_VIEWER_OPTIONS = {
  ...USER_PORTAL_FORMIO_OPTIONS,
  noAlerts: true,
};

/**
 * Same host path as FormioTabRenderer / UserFormView:
 * prepare schema fully, then mount <Form> once (react-formio ignores late form updates).
 */
const FormViewer = () => {
  const { id } = useParams();
  const [formMeta, setFormMeta] = useState(null);
  const [formSchema, setFormSchema] = useState(null);
  const [isInvitationTypesForm, setIsInvitationTypesForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const initialSubmissionRef = useRef({ data: {} });
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchForm = useCallback(async () => {
    setLoading(true);
    setFormSchema(null);
    try {
      const response = await axios.get(`${API_URL}/ems/forms/${id}`);
      const data = response.data;
      let schema = JSON.parse(JSON.stringify(data.schema || { components: [] }));

      try {
        const prepared = await prepareFormSchemaWithInvitationSelects(
          schema,
          API_URL,
          data.form_name
        );
        schema = prepared.schema;
        setIsInvitationTypesForm(
          Boolean(prepared.hasInvitationFormSelects) ||
            formSchemaHasInvitationEntitlementLayout(schema?.components)
        );
      } catch (err) {
        console.warn('Invitation select prep failed — using base schema:', err);
        setIsInvitationTypesForm(
          formSchemaHasInvitationEntitlementLayout(schema?.components)
        );
      }

      if (Array.isArray(schema?.components)) {
        try {
          stampPersonalSettingsRadioLayoutClasses(schema.components);
          applyStandardSchemaTransforms(schema.components);
          ensureDatagridRowActions(schema.components);
          ensureDatagridCellLabels(schema.components);
          // Guest / Travel / Accommodation: show one blank row (not only "+ Add").
          ensureDatagridPreviewRows(schema.components);
        } catch (transformErr) {
          console.warn('Form schema transform failed — rendering raw schema:', transformErr);
        }
      }

      const previewData = Array.isArray(schema?.components)
        ? buildDatagridPreviewSubmissionData(schema.components)
        : {};
      // Guest form often binds grid as dataGrid and/or guestDetails.dataGrid
      if (previewData.dataGrid && !previewData.guestDetails) {
        previewData.guestDetails = { dataGrid: previewData.dataGrid };
      } else if (previewData.guestDetails?.dataGrid == null && previewData.dataGrid) {
        previewData.guestDetails = {
          ...(previewData.guestDetails || {}),
          dataGrid: previewData.dataGrid,
        };
      }

      setFormMeta(data);
      setFormSchema(schema);
      initialSubmissionRef.current = { data: previewData };
    } catch (error) {
      console.error('Error fetching form:', error);
      setFormMeta(null);
      setFormSchema(null);
    } finally {
      setLoading(false);
    }
  }, [id, API_URL]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  if (loading) {
    return (
      <div className="user-form-loading form-viewer-page" aria-busy="true" aria-label="Loading form">
        <Spinner animation="border" role="status" style={{ color: '#d9a128', width: 36, height: 36 }} />
        <span className="user-form-loading__text">Loading form…</span>
      </div>
    );
  }
  if (!formMeta || !formSchema) return <p>Form not found.</p>;

  const formNameClass = formNameToCssClass(formMeta.form_name);
  const formKey = `form-view-${id}-prepared`;

  return (
    <div
      className={[
        'user-layout',
        'user-form-prototype',
        'form-viewer-page',
        'form-viewer-page--no-field-status',
        formNameClass,
        isInvitationTypesForm ? 'invitation-types-form' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="form-viewer-page__header">
        <h1 className="form-viewer-page__title">{formMeta.form_name}</h1>
      </header>

      <div className="user-form-scroll form-viewer-page__scroll">
        <div
          className={[
            'formio-tab-container',
            'form-viewer-page__form',
            'user-form-body-enter',
            formNameClass,
            isInvitationTypesForm ? 'invitation-types-form' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Form
            key={formKey}
            form={formSchema}
            submission={initialSubmissionRef.current}
            options={FORM_VIEWER_OPTIONS}
          />
        </div>
      </div>
    </div>
  );
};

export default FormViewer;
